import { Alert, Button, Col, Descriptions, Form, Input, Row, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { escapeRegExp } from 'es-toolkit/string';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { Cheat, Config, Scheduler, User } from '../api/go';
import type {
  AdminOperatorLog,
  AutoBanLog,
  ConfigItem,
  PDItem,
  SuspiciousInfoResponse,
  SuspiciousMonthStat,
  SuspiciousReplayReportMonthStat,
} from '../api/go';
import { flattenProcessDataReport, processDataColumns } from './process_data_table';

interface QueryForm {
  userId: string;
}

enum AutoBanType {
  HRNA_UUID_HARDWARE = 'hrna_uuid_hardware',
  PROCESS_KEYWORD = 'process_keyword',
  MAP_REPORT = 'map_report',
  PROCESS_V2 = 'process_report_v2',
}

const visibleAutoBanTypes = [AutoBanType.HRNA_UUID_HARDWARE, AutoBanType.PROCESS_KEYWORD, AutoBanType.MAP_REPORT, AutoBanType.PROCESS_V2];

const visibleAutoBanTypeSet = new Set<string>(visibleAutoBanTypes);

interface ProcessReportV2Data {
  type: 'process_report_v2';
  categories: {
    key: string;
    name: string;
    matched_log_ids: number[];
  }[];
}

interface HrnaAutoBanData {
  uuid: string;
  related_banned_user_ids: string[];
  related_banned_count: number;
}

interface RelatedHrnaUserInfo {
  userId: string;
  nick: string;
  status?: number;
  disableRemark: string;
}

interface SuspiciousAutoBanLog extends AutoBanLog {
  extData?: PDItem[];
  hrnaData?: HrnaAutoBanData;
  relatedUsers?: RelatedHrnaUserInfo[];
  uuidList?: string[];
}

const hrnaUserListStyle = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
} as const;

function formatDate(value?: string) {
  if (!value) {
    return '-';
  }

  const date = DateTime.fromISO(value);
  if (!date.isValid) {
    return value;
  }

  return date.toFormat('yyyy-MM-dd HH:mm:ss');
}

function formatJSON(raw: string) {
  if (!raw) {
    return '-';
  }

  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function renderStatus(status: number) {
  if (status === 0) {
    return <Tag color="red">封禁</Tag>;
  }

  if (status === 1) {
    return <Tag color="green">正常</Tag>;
  }

  return <Tag>未知</Tag>;
}

function getAccountStatusText(status?: number) {
  if (status === undefined) {
    return '-';
  }

  if (status === 0) {
    return '封禁';
  }

  if (status === 1) {
    return '正常';
  }

  return '未知';
}

function parseJSON<T>(raw: string): T | null {
  try {
    const parsed = JSON.parse(raw) as T;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function parseHrnaAutoBanData(data: string): HrnaAutoBanData | null {
  const parsed = parseJSON<Partial<HrnaAutoBanData>>(data);
  if (!parsed || typeof parsed.uuid !== 'string' || !Array.isArray(parsed.related_banned_user_ids)) {
    return null;
  }

  const relatedUserIds = parsed.related_banned_user_ids.filter((userId): userId is string => typeof userId === 'string' && userId !== '');
  return {
    uuid: parsed.uuid,
    related_banned_user_ids: relatedUserIds,
    related_banned_count: typeof parsed.related_banned_count === 'number' ? parsed.related_banned_count : relatedUserIds.length,
  };
}

function parseProcessKeywordLogIds(data: string) {
  const parsed = parseJSON<{ matched_process_log_ids?: unknown[] }>(data);
  if (!parsed || !Array.isArray(parsed.matched_process_log_ids)) {
    return [];
  }

  return parsed.matched_process_log_ids.filter((id): id is number => typeof id === 'number' && Number.isFinite(id));
}

function parseProcessReportV2Data(data: string): ProcessReportV2Data | null {
  const parsed = parseJSON<Partial<ProcessReportV2Data>>(data);
  if (!parsed || !Array.isArray(parsed.categories)) {
    return null;
  }

  return {
    type: 'process_report_v2',
    categories: parsed.categories
      .filter((category) => category && typeof category === 'object')
      .map((category) => ({
        key: typeof category.key === 'string' ? category.key : '',
        name: typeof category.name === 'string' ? category.name : '',
        matched_log_ids: Array.isArray(category.matched_log_ids)
          ? category.matched_log_ids.filter((id): id is number => typeof id === 'number' && Number.isFinite(id))
          : [],
      })),
  };
}

function parseProcessKeywordList(configPD: ConfigItem | null) {
  if (!configPD) {
    return [];
  }

  const parsed = parseJSON<{ keyword_list?: unknown[] }>(configPD.config);
  if (!parsed || !Array.isArray(parsed.keyword_list)) {
    return [];
  }

  return parsed.keyword_list.filter((keyword): keyword is string => typeof keyword === 'string' && keyword !== '');
}

function renderHighlightedText(value: string, keywordList: string[]) {
  if (!value) {
    return '-';
  }

  const keywords = Array.from(
    new Set(
      keywordList
        .flatMap((keyword) => keyword.split('%'))
        .map((keyword) => keyword.trim())
        .filter(Boolean),
    ),
  ).sort((left, right) => right.length - left.length);
  if (keywords.length === 0) {
    return value;
  }

  const keywordLookup = new Set(keywords.map((keyword) => keyword.toLowerCase()));
  const regex = new RegExp(`(${keywords.map((keyword) => escapeRegExp(keyword)).join('|')})`, 'gi');

  return value.split(regex).map((part, index) => {
    if (keywordLookup.has(part.toLowerCase())) {
      return (
        <mark key={`${part}-${index}`} style={{ backgroundColor: '#ff7875', padding: 0 }}>
          {part}
        </mark>
      );
    }

    return part;
  });
}

function getAutoBanTypeText(type: string) {
  if (type === AutoBanType.HRNA_UUID_HARDWARE) {
    return '高风险新账号-硬件关联';
  }
  if (type === AutoBanType.PROCESS_KEYWORD) {
    return '进程信息-关键词';
  }
  if (type === AutoBanType.MAP_REPORT) {
    return '地图上报';
  }
  if (type === AutoBanType.PROCESS_V2) {
    return '进程扫描V2';
  }

  return type || '-';
}

async function loadSuspiciousAutoBanLogs(userId: string): Promise<SuspiciousAutoBanLog[]> {
  const logList = await Scheduler.autoBanLog({
    user_id: userId,
    types: visibleAutoBanTypes,
    page_size: 500,
  });

  const logListEx = logList
    .filter((log) => visibleAutoBanTypeSet.has(log.type))
    .map((log): SuspiciousAutoBanLog => {
      const hrnaData = log.type === AutoBanType.HRNA_UUID_HARDWARE ? parseHrnaAutoBanData(log.data) : null;
      return {
        ...log,
        extData: [],
        hrnaData: hrnaData || undefined,
        relatedUsers: [],
        uuidList: [],
      };
    });

  const relatedUserIdSet = new Set<string>();
  for (const log of logListEx) {
    if (log.type !== AutoBanType.HRNA_UUID_HARDWARE || !log.hrnaData) {
      continue;
    }

    for (const relatedUserId of log.hrnaData.related_banned_user_ids) {
      relatedUserIdSet.add(relatedUserId);
    }
  }

  if (relatedUserIdSet.size > 0) {
    try {
      const userInfoList = await User.infoList(Array.from(relatedUserIdSet));
      const userInfoMap = new Map(userInfoList.map((userInfo) => [userInfo.UserId, userInfo]));

      for (const log of logListEx) {
        if (log.type !== AutoBanType.HRNA_UUID_HARDWARE || !log.hrnaData) {
          continue;
        }

        log.relatedUsers = log.hrnaData.related_banned_user_ids.map((relatedUserId) => {
          const userInfo = userInfoMap.get(relatedUserId);
          return {
            userId: relatedUserId,
            nick: userInfo?.NickName || '-',
            status: userInfo?.Status,
            disableRemark: userInfo?.DisableRemark || '-',
          };
        });
      }
    } catch {
      // 关联用户信息是辅助展示；失败时保留日志，并在渲染时展示占位信息。
    }
  }

  const needsOldHrnaUUID = logListEx.some((log) => log.type === AutoBanType.HRNA_UUID_HARDWARE && !log.hrnaData);
  if (needsOldHrnaUUID) {
    try {
      const osLogList = await User.OSInfoLog(userId);
      const hwSet = new Set<string>();
      for (const osLog of osLogList) {
        if (osLog.data.uuid_hardware) {
          hwSet.add(osLog.data.uuid_hardware);
        }
      }

      const uuidList = Array.from(hwSet).slice(0, 5);
      for (const log of logListEx) {
        if (log.type === AutoBanType.HRNA_UUID_HARDWARE && !log.hrnaData) {
          log.uuidList = uuidList;
        }
      }
    } catch {
      // 旧日志的 UUID 是补充信息；失败时保留 AutoBanLog 主体。
    }
  }

  const logProcessIDMap = new Map<number, number[]>();
  const processIDSet = new Set<number>();
  for (const log of logListEx) {
    if (log.type === AutoBanType.PROCESS_KEYWORD) {
      const ids = parseProcessKeywordLogIds(log.data).slice(0, 3);
      logProcessIDMap.set(log.id, ids);
      ids.forEach((id) => processIDSet.add(id));
    } else if (log.type === AutoBanType.PROCESS_V2) {
      const data = parseProcessReportV2Data(log.data);
      const ids = data ? Array.from(new Set(data.categories.flatMap((category) => category.matched_log_ids))).slice(0, 1) : [];
      logProcessIDMap.set(log.id, ids);
      ids.forEach((id) => processIDSet.add(id));
    }
  }

  if (processIDSet.size > 0) {
    try {
      const processList = await Scheduler.processDataList(Array.from(processIDSet));
      const processMap = new Map(processList.map((item) => [item.ID, item]));
      for (const log of logListEx) {
        const ids = logProcessIDMap.get(log.id) || [];
        log.extData = ids.map((id) => processMap.get(id)).filter((item): item is PDItem => Boolean(item));
      }
    } catch {
      // 进程明细失败时仍展示 AutoBanLog 原始内容。
    }
  }

  return logListEx;
}

function renderAutoBanLogData(record: SuspiciousAutoBanLog, keywordList: string[]) {
  if (record.type === AutoBanType.HRNA_UUID_HARDWARE) {
    if (record.hrnaData) {
      const relatedUsers =
        record.relatedUsers && record.relatedUsers.length > 0
          ? record.relatedUsers
          : record.hrnaData.related_banned_user_ids.map((relatedUserId) => ({
              userId: relatedUserId,
              nick: '-',
              status: undefined,
              disableRemark: '-',
            }));
      const userIdWidth = Math.max(...relatedUsers.map((relatedUser) => relatedUser.userId.length), 0);

      return (
        <div>
          <div>关联 UUID: {record.hrnaData.uuid}</div>
          <div>关联封禁人数: {record.hrnaData.related_banned_count}</div>
          <div>关联 user_id</div>
          <div style={hrnaUserListStyle}>
            {relatedUsers.map((relatedUser) => (
              <div key={relatedUser.userId}>
                {relatedUser.userId.padEnd(userIdWidth, ' ')} / {relatedUser.nick || '-'} / {getAccountStatusText(relatedUser.status)} /{' '}
                {relatedUser.disableRemark || '-'}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (record.uuidList && record.uuidList.length > 0) {
      return (
        <div>
          <div>历史使用硬件 UUID</div>
          {record.uuidList.map((uuid) => (
            <div key={uuid}>{uuid}</div>
          ))}
        </div>
      );
    }
  }

  if (record.type === AutoBanType.PROCESS_KEYWORD) {
    if (record.extData && record.extData.length > 0) {
      return (
        <div style={{ wordBreak: 'break-all' }}>
          {record.extData.map((item) => (
            <div key={item.ID}>{renderHighlightedText(item.ProcessData, keywordList)}</div>
          ))}
        </div>
      );
    }

    return <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{formatJSON(record.data)}</pre>;
  }

  if (record.type === AutoBanType.PROCESS_V2) {
    const data = parseProcessReportV2Data(record.data);
    if (!data) {
      return <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{formatJSON(record.data)}</pre>;
    }

    const categoryNames = data.categories.map((category) => category.name).filter(Boolean);
    return (
      <div style={{ wordBreak: 'break-all' }}>
        <div>命中类型 {categoryNames.length > 0 ? JSON.stringify(categoryNames) : '-'}</div>
        {record.extData?.map((item) => (
          <div key={item.ID}>{item.ProcessData}</div>
        ))}
      </div>
    );
  }

  if (record.type === AutoBanType.MAP_REPORT) {
    return <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{formatJSON(record.data)}</pre>;
  }

  return <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{formatJSON(record.data)}</pre>;
}

function createAutoBanLogColumns(keywordList: string[]): ColumnsType<SuspiciousAutoBanLog> {
  return [
    {
      title: '时间',
      dataIndex: 'date',
      key: 'date',
      width: 180,
      render: (value: string) => formatDate(value),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 180,
      render: (value: string) => getAutoBanTypeText(value),
    },
    {
      title: '日志内容',
      dataIndex: 'data',
      key: 'data',
      render: (_, record) => renderAutoBanLogData(record, keywordList),
    },
  ];
}

const monthStatColumns: ColumnsType<SuspiciousMonthStat> = [
  {
    title: '月份',
    dataIndex: 'month',
    key: 'month',
    width: 110,
  },
  {
    title: 'TT',
    key: 'dota_cnt',
    width: 90,
    render: (_, record) => record.game_stat.dota_cnt,
  },
  {
    title: 'OMG',
    key: 'omg_cnt',
    width: 90,
    render: (_, record) => record.game_stat.omg_cnt,
  },
  {
    title: 'MD',
    key: 'md_cnt',
    width: 90,
    render: (_, record) => record.game_stat.md_cnt,
  },
  {
    title: 'RPG',
    key: 'rpg_cnt',
    width: 90,
    render: (_, record) => record.game_stat.rpg_cnt,
  },
  {
    title: '其他',
    key: 'other_cnt',
    width: 90,
    render: (_, record) => record.game_stat.other_cnt,
  },
  {
    title: '总对局',
    key: 'total_cnt',
    width: 100,
    render: (_, record) => record.game_stat.total_cnt,
  },
  {
    title: '开图举报 UV',
    dataIndex: 'cheating_report_uv',
    key: 'cheating_report_uv',
    width: 130,
  },
  {
    title: '局均开图举报',
    dataIndex: 'report_per_game',
    key: 'report_per_game',
    width: 140,
    render: (value: number) => value.toFixed(4),
  },
];

const replayReportMonthStatColumns: ColumnsType<SuspiciousReplayReportMonthStat> = [
  {
    title: '月份',
    dataIndex: 'month',
    key: 'month',
    width: 120,
  },
  {
    title: '总记录数',
    dataIndex: 'total_count',
    key: 'total_count',
    width: 140,
  },
  {
    title: '有问题记录数',
    dataIndex: 'problem_count',
    key: 'problem_count',
    width: 160,
  },
];

const operatorLogColumns: ColumnsType<AdminOperatorLog> = [
  {
    title: '时间',
    dataIndex: 'date',
    key: 'date',
    width: 180,
    render: (value: string) => formatDate(value),
  },
  {
    title: '操作人',
    dataIndex: 'admin_name',
    key: 'admin_name',
    width: 160,
    render: (value: string, record) => value || record.admin_id || '-',
  },
  {
    title: '操作类型',
    dataIndex: 'type',
    key: 'type',
    width: 240,
  },
  {
    title: '内容',
    dataIndex: 'data',
    key: 'data',
    render: (value: string) => (
      <Typography.Text style={{ maxWidth: 720 }} ellipsis>
        {value || '-'}
      </Typography.Text>
    ),
  },
];

function UserOverview({ data }: { data: SuspiciousInfoResponse }) {
  const user = data.user;

  return (
    <Descriptions
      bordered
      size="small"
      column={2}
      items={[
        { key: 'user_id', label: '用户 ID', children: user.UserId },
        { key: 'nick_name', label: '昵称', children: user.NickName || '-' },
        { key: 'status', label: '账号状态', children: renderStatus(user.Status) },
        { key: 'disable_remark', label: '封号备注', children: user.DisableRemark || '-' },
        { key: 'user_regtime', label: '注册时间', children: formatDate(user.UserRegtime) },
      ]}
    />
  );
}

function BanLogInfo({ data }: { data: SuspiciousInfoResponse }) {
  const banLog = data.ban_log;
  if (!data.is_banned && !banLog) {
    return null;
  }

  if (!banLog) {
    return null;
  }

  return (
    <Descriptions
      bordered
      size="small"
      column={2}
      items={[
        { key: 'date', label: '封号时间', children: formatDate(banLog.date) },
        { key: 'reason', label: '封号原因', children: banLog.ban_reason || '-' },
        { key: 'nick', label: '封号昵称', children: banLog.user_nick || '-' },
        { key: 'ban_type', label: '封号类型', children: banLog.ban_type || '-' },
        {
          key: 'hardware',
          label: '硬件 UUID',
          children: banLog.parsed_device_info?.uuid_hardware?.join(' / ') || '-',
        },
        {
          key: 'os',
          label: '系统 UUID',
          children: banLog.parsed_device_info?.uuid_os?.join(' / ') || '-',
        },
        {
          key: 'device_info',
          label: '设备信息',
          span: 2,
          children: (
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', textAlign: 'left' }}>
              {formatJSON(banLog.device_info)}
            </pre>
          ),
        },
      ]}
    />
  );
}

export default function SuspiciousInfoPage() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SuspiciousInfoResponse | null>(null);
  const [autoBanLogLoading, setAutoBanLogLoading] = useState(false);
  const [autoBanLogList, setAutoBanLogList] = useState<SuspiciousAutoBanLog[]>([]);
  const [autoBanConfigPD, setAutoBanConfigPD] = useState<ConfigItem | null>(null);

  async function handleQuery(values: QueryForm) {
    const userId = values.userId.trim();
    if (!userId) {
      message.error('请输入 user_id');
      return;
    }

    setLoading(true);
    setAutoBanLogLoading(true);
    setAutoBanLogList([]);
    setAutoBanConfigPD(null);
    try {
      const nextData = await Cheat.suspiciousInfo(userId);
      setData(nextData);

      try {
        const [nextAutoBanLogList, nextAutoBanConfigPD] = await Promise.all([
          loadSuspiciousAutoBanLogs(userId),
          Config.getConfig('process_data_task_config').catch(() => null),
        ]);
        setAutoBanLogList(nextAutoBanLogList);
        setAutoBanConfigPD(nextAutoBanConfigPD);
      } catch {
        setAutoBanLogList([]);
        setAutoBanConfigPD(null);
        message.error('AutoBanLog 查询失败');
      }
    } catch {
      setData(null);
      setAutoBanLogList([]);
      setAutoBanConfigPD(null);
      message.error('查询失败，请确认 user_id 是否存在');
    } finally {
      setAutoBanLogLoading(false);
      setLoading(false);
    }
  }

  const processRows = data ? flattenProcessDataReport(data.process_report) : [];
  const replayReportRows = data?.replay_report_month_stats ?? [];
  const replayReportTotalCount = replayReportRows.reduce((sum, row) => sum + row.total_count, 0);
  const replayReportProblemCount = replayReportRows.reduce((sum, row) => sum + row.problem_count, 0);
  const autoBanLogColumns = createAutoBanLogColumns(parseProcessKeywordList(autoBanConfigPD));

  return (
    <div>
      <Row>
        <Col span={10}>
          <Form<QueryForm>
            name="suspiciousInfo"
            layout="horizontal"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            style={{ maxWidth: 720 }}
            onFinish={handleQuery}
            autoComplete="off"
          >
            <Form.Item<QueryForm> label="User ID" name="userId" required>
              <Input placeholder="请输入 user_id" disabled={loading} />
            </Form.Item>
            <Form.Item label={null}>
              <Button type="primary" htmlType="submit" loading={loading}>
                查询
              </Button>
            </Form.Item>
          </Form>
        </Col>
      </Row>

      {data ? (
        <Space direction="vertical" size={20} style={{ display: 'flex', textAlign: 'left' }}>
          {data.warning ? <Alert type="warning" showIcon message={data.warning} /> : null}

          <div>
            <Typography.Title level={4}>用户信息</Typography.Title>
            <UserOverview data={data} />
          </div>

          {data.ban_log ? (
            <div>
              <Typography.Title level={4}>封号操作信息</Typography.Title>
              <BanLogInfo data={data} />
            </div>
          ) : null}

          <div>
            <Typography.Title level={4}>AutoBanLog</Typography.Title>
            <Table<SuspiciousAutoBanLog>
              rowKey="id"
              dataSource={autoBanLogList}
              columns={autoBanLogColumns}
              loading={autoBanLogLoading}
              pagination={false}
              scroll={{ x: 1000 }}
            />
          </div>

          <div>
            <Typography.Title level={4}>对局与举报</Typography.Title>
            <Table<SuspiciousMonthStat>
              rowKey="month"
              dataSource={data.month_stats}
              columns={monthStatColumns}
              pagination={false}
              scroll={{ x: 1000 }}
            />
          </div>

          <div>
            <Typography.Title level={4}>地图上报报告</Typography.Title>
            <Typography.Text>
              总记录 {replayReportTotalCount} 条，有问题记录 {replayReportProblemCount} 条，时间范围 {formatDate(data.process_report.start)}{' '}
              至 {formatDate(data.process_report.end)}
            </Typography.Text>
            <Table<SuspiciousReplayReportMonthStat>
              rowKey="month"
              style={{ marginTop: 12 }}
              dataSource={replayReportRows}
              columns={replayReportMonthStatColumns}
              pagination={false}
              scroll={{ x: 520 }}
            />
          </div>

          <div>
            <Typography.Title level={4}>可疑进程</Typography.Title>
            <Typography.Text>
              原始日志 {data.process_report.fetched_log_count} 条，命中日志 {processRows.length} 条，时间范围{' '}
              {formatDate(data.process_report.start)} 至 {formatDate(data.process_report.end)}
            </Typography.Text>
            <Table
              rowKey="log_id"
              style={{ marginTop: 12 }}
              dataSource={processRows}
              columns={processDataColumns}
              scroll={{ x: 1600 }}
              pagination={false}
            />
          </div>

          <div>
            <Typography.Title level={4}>操作日志</Typography.Title>
            <Table<AdminOperatorLog>
              rowKey="id"
              dataSource={data.operator_logs}
              columns={operatorLogColumns}
              scroll={{ x: 1100 }}
              pagination={false}
              expandable={{
                expandedRowRender: (record) => (
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{formatJSON(record.data)}</pre>
                ),
              }}
            />
          </div>
        </Space>
      ) : null}
    </div>
  );
}
